<template>
    <div
        ref="root"
        class="px-4 py-4 md:px-6"
    >
        <div
            class="flex flex-col gap-4"
            @click="toggleOrderSummary"
        >
            <prime-message
                v-if="expiredContracts.length > 0"
                severity="error"
                class="!my-0"
                :closable="false"
            >
                Oops! At least one item in your cart is expired. Please edit the
                item to confirm booking availability before checking out.
            </prime-message>

            <div class="flex items-center gap-2">
                <prime-button
                    class="xl:hidden"
                    text
                    severity="secondary"
                >
                    <i
                        v-if="orderSummaryShown"
                        class="pi pi-chevron-down"
                    />
                    <i
                        v-else
                        class="pi pi-chevron-right"
                    />
                </prime-button>

                <div class="flex-1 text-lg font-medium text-gray-900">
                    Order Summary
                </div>

                <div class="text-right xl:hidden">
                    {{ total }}
                    <div v-if="hasDeposit">
                        <span class="bg-yellow-300 px-1"
                            >Due Today:
                            {{ dueToday }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div :class="orderSummaryShown ? 'block' : 'hidden xl:block'">
            <div class="divide-y-1 mb-4 space-y-3 md:p-3 lg:py-6">
                <div
                    v-for="item in checkoutData.items"
                    :key="item.key"
                >
                    <contract-cart-item
                        v-if="item.__typename === 'CartContractItem'"
                        :item="item"
                    />

                    <prime-message
                        v-else
                        severity="error"
                    >
                        Whoops! You have an item in your cart we do not know how
                        to display.
                    </prime-message>
                </div>
            </div>

            <div class="md:px-3">
                <div class="flex items-center justify-between text-lg">
                    <span class="font-medium text-gray-900">Subtotal</span>
                    <span class="text-gray-900">{{ subtotal }}</span>
                </div>

                <div
                    v-if="inclusiveTaxTotal.isNotZero()"
                    class="flex items-center justify-between md:mt-6"
                >
                    <span class="text-gray-800">Inclusive Tax</span>
                    <span class="text-gray-800">{{
                        inclusiveTaxTotal.formatCurrency()
                    }}</span>
                </div>

                <div class="flex items-center justify-between">
                    <span class="text-gray-800">Tax</span>
                    <span class="text-gray-800">{{ exclusiveTaxTotal }}</span>
                </div>

                <div v-if="showTaxDetailsToggle && !taxDetailsShown">
                    <prime-button
                        link
                        class="!p-0"
                        size="small"
                        @click="taxDetailsShown = true"
                    >
                        Show Tax Details...
                    </prime-button>
                </div>

                <template v-if="taxDetailsShown">
                    <div
                        v-for="taxLineItem in checkoutData.salesTaxItems"
                        :key="taxLineItem.key"
                        class="flex items-center justify-between text-sm"
                    >
                        <span class="text-700">{{
                            taxLineItem.description
                        }}</span>
                        <span class="text-700">{{
                            formatCurrency(taxLineItem.total)
                        }}</span>
                    </div>
                    <div>
                        <prime-button
                            link
                            class="!p-0"
                            size="small"
                            @click="taxDetailsShown = false"
                        >
                            Hide Tax Details
                        </prime-button>
                    </div>
                </template>

                <div class="mt-2 flex items-center justify-between md:mt-6">
                    <span class="text-xl font-medium text-gray-900">
                        {{ totalLanguage }}
                    </span>
                    <span class="text-xl font-medium text-gray-900">{{
                        total
                    }}</span>
                </div>

                <div
                    v-if="hasDeposit"
                    class="mt-2 flex items-center justify-between md:mt-6"
                >
                    <span
                        class="bg-yellow-300 px-1 text-xl font-medium text-gray-900"
                    >
                        Due Today
                    </span>
                    <span class="text-xl font-medium text-gray-900">{{
                        dueToday
                    }}</span>
                </div>

                <gift-card-line-item
                    v-for="giftCard in giftCards"
                    :key="giftCard.token"
                    :gift-card="giftCard"
                />

                <div
                    v-if="storeCreditTotal.isPositiveAndNotZero()"
                    class="mt-3 flex items-center justify-between"
                >
                    <div class="text-gray-800">Store Credit</div>
                    <span class="text-gray-800">
                        {{ storeCreditTotalFormatted }}
                    </span>
                </div>

                <div
                    v-if="
                        giftCardsPaymentTotal.isPositiveAndNotZero() ||
                        storeCreditTotal.isPositiveAndNotZero()
                    "
                    class="mt-2 flex items-center justify-between md:mt-6"
                >
                    <span class="text-xl font-bold text-gray-900"
                        >Payment Total</span
                    >
                    <span class="text-2xl font-bold text-gray-900">{{
                        paymentAmount
                    }}</span>
                </div>

                <div
                    v-if="surchargeNote !== ''"
                    class="mt-2"
                >
                    <content-reset :body="surchargeNote" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const CheckoutCartFragment = graphql(/* GraphQL */ `
    fragment CheckoutCartFragment on Checkout {
        subtotal
        total
        dueToday
        surchargeNote(language: ENGLISH)
        inclusiveTaxTotal
        exclusiveTaxTotal
        salesTaxItems {
            key
            description
            total
        }
        items {
            __typename
            ... on CartContractItem {
                key
                contract {
                    __typename
                    ... on PlanContract {
                        draftExpiresAtUtc
                    }
                }
                ...CartSidebarContractCartItemFragment
            }
            ... on CartProductItem {
                key
            }
        }
    }
`)
</script>

<script setup lang="ts">
import PrimeMessage from 'primevue/message'
import PrimeButton from 'primevue/button'
import { Decimal } from '~/composables/support/decimal'
import ContractCartItem from '~/components/CartSidebar/ContractCartItem.vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { computed, onMounted, ref } from 'vue'
import { useCurrentDate } from '~/composables/use-current-date'
import { isAfter } from 'date-fns'
import { formatCurrency, parseUtc } from '~/composables/support/localization'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useCheckoutPaymentData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import GiftCardLineItem from '~/components/Checkout/Cart/GiftCardLineItem.vue'
import ContentReset from '~/components/ContentReset.vue'

const props = defineProps<{
    checkout: FragmentType<typeof CheckoutCartFragment>
    balanceDueMode?: boolean
}>()

const root = ref<HTMLDivElement>()
onMounted(() => {
    if (isKiosk.value) {
        root.value?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        })
    }
})

const checkoutData = computed(() =>
    getFragmentData(CheckoutCartFragment, props.checkout)
)

const currentDate = useCurrentDate()
const expiredContracts = computed(() => {
    return checkoutData.value.items.filter(item => {
        if (item.__typename === 'CartContractItem') {
            if (item.contract.__typename === 'PlanContract') {
                if (item.contract.draftExpiresAtUtc === null) {
                    return false
                }

                if (
                    isAfter(
                        currentDate.value,
                        parseUtc(item.contract.draftExpiresAtUtc)
                    )
                ) {
                    return true
                }
            }
        }

        return false
    })
})

const subtotal = computed(() =>
    new Decimal(checkoutData.value.subtotal).formatCurrency()
)

const inclusiveTaxTotal = computed(
    () => new Decimal(checkoutData.value.inclusiveTaxTotal)
)

const exclusiveTaxTotal = computed(() =>
    new Decimal(checkoutData.value.exclusiveTaxTotal).formatCurrency()
)

const total = computed(() =>
    new Decimal(checkoutData.value.total).formatCurrency()
)

const dueToday = computed(() =>
    new Decimal(checkoutData.value.dueToday).formatCurrency()
)

const hasDeposit = computed(() => {
    return new Decimal(checkoutData.value.total).isNotEqualTo(
        new Decimal(checkoutData.value.dueToday)
    )
})

const { giftCards, storeCredit } = useCheckoutPaymentData()
const giftCardsPaymentTotal = computed(() => {
    return giftCards.value.reduce(
        (accumulator, giftCard) =>
            accumulator.plus(new Decimal(giftCard.amount)),
        Decimal.zero()
    )
})

const storeCreditTotal = computed(() => {
    return new Decimal(storeCredit.value.amountUsed)
})

const paymentAmount = computed(() => {
    const checkoutTotal = new Decimal(checkoutData.value.dueToday)
    return checkoutTotal
        .minus(giftCardsPaymentTotal.value)
        .minus(storeCreditTotal.value)
        .formatCurrency()
})

const storeCreditTotalFormatted = computed(() => {
    return storeCreditTotal.value.invertSign().formatCurrency(2, false)
})

const showTaxDetailsToggle = computed(
    () => checkoutData.value.salesTaxItems.length > 1
)

const { isKiosk } = useLayoutMode()
const orderSummaryShown = ref(
    isKiosk.value ? true : expiredContracts.value.length > 0
)
function toggleOrderSummary() {
    orderSummaryShown.value = !orderSummaryShown.value
}

const taxDetailsShown = ref(false)

const totalLanguage = computed(() =>
    props.balanceDueMode ? 'Balance Due Today' : 'Order Total'
)

const surchargeNote = computed(() => {
    if (checkoutData.value === undefined) {
        return ''
    }

    return checkoutData.value.surchargeNote
})
</script>
